import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useParams } from 'react-router-dom';
import { getShortIdFromUrl, isShortId } from 'utils/ShortIdHelper';
import { STATUS_INITIAL, STATUS_NOT_FOUND, STATUS_SUCCESS } from '~/constants';
import { useCurrencyContext } from '~/context/CurrencyContext';
import { useI18n } from '~/stores/i18n';

import reducer, { initialState } from './reducer';
import actionsFactory from './reducer/actions';

const initialContext = {
  ...initialState,
  experienceActions: actionsFactory(() => {}),
};

const ExperienceContext = createContext(initialContext);
const useExperienceContext = () => useContext(ExperienceContext);

function ExperienceContextProvider({ children }) {
  const params = useParams<{ id: string }>();
  const shortId = getShortIdFromUrl(params.id);
  const { state: currencyState } = useCurrencyContext();

  const { lang: langCode } = useI18n();

  const [state, dispatch] = useReducer(reducer, initialState);
  const experienceActions = useMemo(() => actionsFactory(dispatch), [dispatch]);

  const contextValue = { ...state, experienceActions };

  useEffect(() => {
    if (state.status !== STATUS_NOT_FOUND && !isShortId(shortId)) {
      experienceActions.setNotFound(shortId, langCode);
    } else if (
      (state.shortId !== shortId || langCode !== state.lang) &&
      (state.status === STATUS_SUCCESS || state.status === STATUS_INITIAL)
    ) {
      experienceActions.fetchExperience(
        shortId,
        langCode,
        currencyState.currency,
      );
    }
  }, [experienceActions, shortId, state, currencyState.currency, langCode]);

  return (
    <ExperienceContext.Provider value={contextValue}>
      {children}
    </ExperienceContext.Provider>
  );
}

export { useExperienceContext, ExperienceContextProvider };
